import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { type BarSeriesType } from '@mui/x-charts';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import type { ChartsContainerProps } from '@mui/x-charts/ChartsContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { AlertTriangle, BarChart3, Eye } from 'lucide-react';

import { formatCompactNumber } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

import { CHART_TOOLTIP_SX, ChartEmptyState, InfoTip } from './ChartControls';
import type { Granularity } from './chartUtils';
import { type PlacementKey, type ViewChannelKey } from './impressionsSeries';
import { useListingImpressions } from './useListingImpressions';

type SeriesMeta = { label: string; help: string };

// Embeddable chart for the funnel's Impressions / Listing views steps. The
// metric is fixed by which step is expanded — Impressions shows the stacked
// breakdown by placement (GA4 `view_item_list`), Listing views shows the
// stacked breakdown by traffic-source channel (GA4 `view_item`). Both render
// the same way, so the hover tooltip lists each segment for the day.
export const ListingImpressionsBody = ({
  lotId,
  start,
  end,
  granularity,
  metric = 'impressions',
}: {
  lotId: string;
  start: Date;
  end: Date;
  granularity: Granularity;
  metric?: 'impressions' | 'views';
}) => {
  const { t, locale } = useLocale();

  const { labels, barSeries, viewsSeries, loading, error } =
    useListingImpressions(lotId, start, end, granularity);

  if (error != null) {
    return (
      <ChartEmptyState
        icon={<AlertTriangle size={22} />}
        message={t('Failed to load data')}
      />
    );
  }

  const showImpressions = metric === 'impressions';

  // Per-placement labels + tooltips for the impressions breakdown.
  const placementMeta: Record<PlacementKey, SeriesMeta> = {
    search_results: {
      label: t('Search results'),
      help: t(
        'Times this listing appeared in the search results on RealAdvisor.',
      ),
    },
    similar_listings: {
      label: t('Similar listings'),
      help: t(
        'Times this listing appeared in the "similar listings" section of another listing.',
      ),
    },
    agency_profile: {
      label: t('Agency profile'),
      help: t(
        "Times this listing appeared on an agency's public profile page.",
      ),
    },
    landing_page: {
      label: t('Landing page'),
      help: t('Times this listing appeared on a marketing landing page.'),
    },
    other: {
      label: t('Other / unknown'),
      help: t('Impressions with no recognised placement in GA4.'),
    },
  };

  // Per-channel labels + tooltips for the listing-views breakdown. The
  // channel reflects how GA4 ORIGINALLY acquired the viewer (their
  // first-ever visit), not necessarily how they reached this specific
  // listing — see the comment in useListingImpressions.ts.
  const channelMeta: Record<ViewChannelKey, SeriesMeta> = {
    organic: {
      label: t('Organic search'),
      help: t(
        'Viewers originally acquired via an unpaid search engine result.',
      ),
    },
    paid: {
      label: t('Paid'),
      help: t('Viewers originally acquired via a paid ad (search or social).'),
    },
    email: {
      label: t('Email'),
      help: t('Viewers originally acquired via an email or newsletter.'),
    },
    social: {
      label: t('Social'),
      help: t('Viewers originally acquired via a social network.'),
    },
    referral: {
      label: t('Referral'),
      help: t('Viewers originally acquired via a link on another website.'),
    },
    ai: {
      label: t('AI assistant'),
      help: t(
        'Viewers originally acquired via an AI assistant such as ChatGPT.',
      ),
    },
    direct: {
      label: t('Direct'),
      help: t(
        'Viewers who originally arrived directly — typed the URL, bookmark, or app.',
      ),
    },
    other: {
      label: t('Other / unknown'),
      help: t('Views with no original acquisition channel recorded in GA4.'),
    },
  };

  // The segments to plot — placements for impressions, channels for views
  // (dropping all-zero channels so the legend and tooltip stay tight).
  const segments = showImpressions
    ? barSeries
        .filter(serie => serie.data.some(value => value > 0))
        .map(bar => ({ ...bar, ...placementMeta[bar.key] }))
    : viewsSeries
        .filter(serie => serie.data.some(value => value > 0))
        .map(serie => ({ ...serie, ...channelMeta[serie.key] }));

  const series: BarSeriesType[] = segments.map(segment => ({
    type: 'bar',
    data: segment.data,
    label: segment.label,
    stack: 'value',
    color: segment.color,
    yAxisId: 'value',
  }));

  const yAxis: NonNullable<ChartsContainerProps['yAxis']> = [
    {
      id: 'value',
      scaleType: 'linear',
      position: 'left',
      valueFormatter: (value: number) => formatCompactNumber(value, locale),
      width: 40,
    },
  ];

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={260} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" width={96} height={16} />
          ))}
        </Box>
      </Stack>
    );
  }

  const total = segments.reduce(
    (sum, segment) => sum + segment.data.reduce((acc, value) => acc + value, 0),
    0,
  );

  if (total === 0) {
    return showImpressions ? (
      <ChartEmptyState
        icon={<BarChart3 size={22} />}
        message={t('No impressions yet')}
      />
    ) : (
      <ChartEmptyState
        icon={<Eye size={22} />}
        message={t('No listing views yet')}
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      <ChartsContainer
        series={series}
        height={260}
        margin={{ top: 16, right: 8, bottom: 8, left: 8 }}
        xAxis={[
          {
            data: labels,
            scaleType: 'band',
            id: 'x',
            position: 'bottom',
            height: 24,
            tickLabelStyle: { fontSize: 10 },
          },
        ]}
        yAxis={yAxis}
      >
        <ChartsGrid horizontal />
        <BarPlot />
        <ChartsXAxis axisId="x" />
        <ChartsYAxis axisId="value" />
        <ChartsAxisHighlight x="band" />
        <ChartsTooltip sx={CHART_TOOLTIP_SX} />
      </ChartsContainer>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {segments.map(segment => (
          <Box
            key={segment.key}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: segment.color,
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {segment.label}
            </Typography>
            <InfoTip title={segment.help} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
};
